import { formatCustomRangeTriggerLabel } from '@components'

import type { Locale } from 'date-fns'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { PeriodMode } from '../../../enums/periodMode'
import { useLocale } from '../../../hooks/useLocale'
import { getLocalDateFromDateString } from '../../../utils/getLocalDateFromDateString'
import { EXPERT_BALANCE_SHEET_COLUMN_WIDTHS } from '../constants/expertBalanceSheetColumnWidths'
import { BalanceSheetAmount } from '../elements/ExpertBalanceSheetTable/elements/BalanceSheetAmount'
import { BalanceSheetLabel } from '../elements/ExpertBalanceSheetTable/elements/BalanceSheetLabel'
import { ExpertBalanceSheetTab } from '../types/expertBalanceSheetView'
import type {
  ExpertBalanceSheetAssetRow,
  ExpertBalanceSheetLiabilityRow,
  ExpertBalanceSheetPeriodColumn,
  ExpertBalanceSheetRowKind,
} from '../types/expertBalanceSheetView'

export type ExpertBalanceSheetColumnAlign = 'end' | 'start'

export interface ExpertBalanceSheetColumnDef<Row> {
  align?: ExpertBalanceSheetColumnAlign | ((row: Row) => ExpertBalanceSheetColumnAlign | undefined)
  Cell: (row: Row) => ReactNode
  header: ReactNode
  highlighted?: boolean
  id: string
  width?: number | string
}

const isAggregateRow = (row: { kind: ExpertBalanceSheetRowKind }): boolean =>
  row.kind === 'subtotal' || row.kind === 'total'

const isAccountRow = (row: { kind: ExpertBalanceSheetRowKind }): boolean => row.kind === 'account'

const formatCustomRangeHeader = (
  period: ExpertBalanceSheetPeriodColumn | undefined,
  periodType: PeriodMode | undefined,
  locale: Locale,
): string | undefined => {
  if (periodType !== PeriodMode.CustomDate) {
    return undefined
  }

  const start = getLocalDateFromDateString(period?.startDate) ?? null
  const end = getLocalDateFromDateString(period?.endDate) ?? null

  return formatCustomRangeTriggerLabel(start, end, locale)
}

export const useExpertBalanceSheetColumns = (
  tab: ExpertBalanceSheetTab,
  periods: ExpertBalanceSheetPeriodColumn[],
  periodType: PeriodMode | undefined,
):
  | ExpertBalanceSheetColumnDef<ExpertBalanceSheetAssetRow>[]
  | ExpertBalanceSheetColumnDef<ExpertBalanceSheetLiabilityRow>[] => {
  const { t } = useTranslation()
  const locale = useLocale()
  const currentPeriod = periods[0]
  const previousPeriod = periods[1]
  const isFiscalYear = periodType === PeriodMode.FiscalYear
  const currentPeriodHeader = isFiscalYear
    ? t('expert_balance_sheet.table.column.period_current')
    : (currentPeriod?.label ?? t('expert_balance_sheet.table.column.period_current'))
  const previousPeriodHeader = isFiscalYear
    ? t('expert_balance_sheet.table.column.period_previous')
    : (previousPeriod?.label ?? t('expert_balance_sheet.table.column.period_previous'))

  return useMemo(() => {
    const widths = EXPERT_BALANCE_SHEET_COLUMN_WIDTHS
    const currentNetHeader =
      formatCustomRangeHeader(currentPeriod, periodType, locale) ??
      t('expert_balance_sheet.table.column.net', { period: currentPeriodHeader })
    const previousNetHeader =
      formatCustomRangeHeader(previousPeriod, periodType, locale) ??
      t('expert_balance_sheet.table.column.net', { period: previousPeriodHeader })
    const itemColumn = {
      align: (row: { kind: ExpertBalanceSheetRowKind }) => (row.kind === 'total' ? ('end' as const) : undefined),
      header: t('expert_balance_sheet.table.column.item'),
      id: 'item',
    }

    if (tab === ExpertBalanceSheetTab.Assets) {
      const assetColumns: ExpertBalanceSheetColumnDef<ExpertBalanceSheetAssetRow>[] = [
        {
          ...itemColumn,
          Cell: (row) => <BalanceSheetLabel accountNumber={row.accountNumber} kind={row.kind} label={row.label} />,
        },
        {
          align: 'end',
          Cell: (row) => <BalanceSheetAmount subdued={isAccountRow(row)} value={row.gross[0]} />,
          header: t('expert_balance_sheet.table.column.gross'),
          id: 'gross',
          width: widths.amount,
        },
        {
          align: 'end',
          Cell: (row) => <BalanceSheetAmount subdued={isAccountRow(row)} value={row.depreciation[0]} />,
          header: t('expert_balance_sheet.table.column.deprec_impair'),
          id: 'depreciation',
          width: widths.amount,
        },
        {
          align: 'end',
          Cell: (row) => (
            <BalanceSheetAmount bold={isAggregateRow(row)} subdued={isAccountRow(row)} value={row.net[0]} />
          ),
          header: currentNetHeader,
          highlighted: true,
          id: 'currentNet',
          width: widths.amount,
        },
        {
          align: 'end',
          Cell: (row) => (
            <BalanceSheetAmount bold={isAggregateRow(row)} subdued={isAccountRow(row)} value={row.net[1]} />
          ),
          header: previousNetHeader,
          id: 'previousNet',
          width: widths.amount,
        },
      ]

      return assetColumns
    }

    const liabilityColumns: ExpertBalanceSheetColumnDef<ExpertBalanceSheetLiabilityRow>[] = [
      {
        ...itemColumn,
        Cell: (row) => <BalanceSheetLabel accountNumber={row.accountNumber} kind={row.kind} label={row.label} />,
      },
      {
        align: 'end',
        Cell: (row) => <BalanceSheetAmount bold={isAggregateRow(row)} subdued={isAccountRow(row)} value={row.net[0]} />,
        header: currentNetHeader,
        highlighted: true,
        id: 'currentNet',
        width: widths.amount,
      },
      {
        align: 'end',
        Cell: (row) => <BalanceSheetAmount bold={isAggregateRow(row)} subdued={isAccountRow(row)} value={row.net[1]} />,
        header: previousNetHeader,
        id: 'previousNet',
        width: widths.amount,
      },
    ]

    return liabilityColumns
  }, [tab, t, currentPeriod, previousPeriod, currentPeriodHeader, previousPeriodHeader, periodType, locale])
}
